import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiHeart } from 'react-icons/fi';
import { toast } from 'react-hot-toast';
import { getProductImage } from '../utils/imageHelpers';
import PlaceholderImage from '../components/PlaceholderImage';
import useWishlistStore from '../store/useWishlistStore';
import useAuthStore from '../store/useAuthStore';
import { useTranslation } from 'react-i18next';
import { getLocalized } from '../utils/i18nHelpers';
import { formatPrice } from '../utils/formatPrice';

const ProductCard = ({ product }) => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { isAuthenticated } = useAuthStore();
    const { isInWishlist, addToWishlist, removeFromWishlist } = useWishlistStore();

    const isWishlisted = isInWishlist(product._id);
    const price = product.salePrice || product.price || 0;
    const hasDiscount = product.salePrice > 0 && product.salePrice < product.price;
    const isNew = new Date(product.createdAt) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
    const hasSecondImage = product.images && product.images.length > 1;

    const handleWishlist = async (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (!isAuthenticated) {
            toast.error(t('products.loginToWishlist'));
            return;
        }

        if (isWishlisted) {
            await removeFromWishlist(product._id);
        } else {
            await addToWishlist(product._id);
        }
    };
    
    // Attempt to get category name or default
    const categoryName = product.category 
        ? (typeof product.category === 'object' ? getLocalized(product.category, 'name', i18n.language) : 'KATEGORİ')
        : (product.material ? product.material : 'ÜRÜN');

    return (
        <div className="group relative bg-white border border-gray-100/50 hover:border-gray-200/80 rounded-lg p-2 md:p-3 transition-all duration-300 hover:shadow-md flex flex-col justify-between h-full">
            <Link to={`/products/${product.slug}`} className="flex flex-col h-full justify-between">
                <div>
                    <div className="relative aspect-square bg-white rounded-md mb-2 md:mb-3 overflow-hidden transition-all duration-300">
                        {product.images && product.images.length > 0 ? (
                            <>
                                <img
                                    src={getProductImage(product.images, 0)}
                                    alt={product.name}
                                    className={`w-full h-full object-cover transition-all duration-750 ${hasSecondImage ? 'group-hover:opacity-0' : 'group-hover:scale-[1.03]'}`}
                                    loading="lazy"
                                    decoding="async"
                                />
                                {hasSecondImage && (
                                    <img
                                        src={getProductImage(product.images, 1)}
                                        alt={product.name}
                                        className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-all duration-750 group-hover:scale-[1.03]"
                                        loading="lazy"
                                        decoding="async"
                                        fetchPriority="low"
                                    />
                                )}
                            </>
                        ) : (
                            <PlaceholderImage type="product" />
                        )}

                        {/* Badges (Top Left) */}
                        {(isNew || hasDiscount) && (
                            <div className="absolute top-0 left-0 flex flex-col z-10">
                                {isNew && (
                                    <span className="bg-[#45b59c] text-white text-[8px] md:text-[9px] font-bold px-1.5 py-0.5 md:px-2 md:py-0.5 tracking-wider uppercase rounded-br">
                                        {t('products.new')}
                                    </span>
                                )}
                                {hasDiscount && product.price > 0 && (
                                    <span className="bg-[#fc4c60] text-white text-[8px] md:text-[9px] font-bold px-1.5 py-0.5 md:px-2 md:py-0.5 tracking-wider uppercase mt-[2px] rounded-br">
                                        {t('products.discount', { rate: Math.round(((product.price - price) / product.price) * 100) })}
                                    </span>
                                )}
                            </div>
                        )}

                        {/* Wishlist Button (Top Right) */}
                        <div className="absolute top-2 right-2 md:top-3 md:right-3 z-20">
                            <button
                                onClick={handleWishlist}
                                className="bg-white/80 backdrop-blur-sm p-1.5 md:p-2 rounded-full shadow-sm text-gray-500 hover:text-red-500 hover:bg-white transition-all duration-200"
                                title={isWishlisted ? t('products.removeFromWishlist') : t('products.addToWishlist')}
                            >
                                <FiHeart className={`text-[12px] md:text-[14px] ${isWishlisted ? 'fill-red-500 text-red-500' : ''}`} />
                            </button>
                        </div>

                        {/* Hover Button (Bottom - Desktop Only) */}
                        <div className="absolute bottom-0 left-0 w-full bg-[#2b303b]/90 backdrop-blur-xs text-white text-[10px] font-semibold py-2.5 text-center uppercase tracking-[0.12em] opacity-0 translate-y-full group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300 z-20 hidden lg:block">
                            {t('products.viewProduct') || 'ÜRÜNÜ İNCELE'}
                        </div>
                    </div>

                    {/* Product Details below image */}
                    <div className="text-left px-1">
                        <p className="text-[#45b59c] text-[8px] md:text-[9px] uppercase tracking-widest mb-1 font-semibold">
                            {categoryName}
                        </p>
                        <h3 className="font-serif text-[#333333] text-[12px] md:text-[13px] leading-tight mb-1.5 line-clamp-2 hover:text-[#45b59c] transition-colors min-h-[32px] md:min-h-[36px]">
                            {product.name}
                        </h3>
                    </div>
                </div>

                <div className="text-left px-1 mt-1">
                    <div className="flex flex-wrap items-baseline gap-1 md:gap-2">
                        <span className="font-bold text-[#2b303b] text-[13px] md:text-[14px] whitespace-nowrap">
                            {formatPrice(price)} ₺
                        </span>
                        {hasDiscount && (
                            <span className="text-[#a0a0a0] text-[10px] md:text-[11px] line-through font-normal whitespace-nowrap">
                                {formatPrice(product.price)} ₺
                            </span>
                        )}
                    </div>
                </div>
            </Link>
        </div>
    );
};

export default ProductCard;
